import { Api } from '@config/api';
import { useChatStatus } from '@helpers/useChatStatus';
import type React from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Blocker } from '../components/Blocker';
import ChatTitle from '../components/ChatTitle';
import MessageBox from '../components/MessageBox';
import Messages from '../components/Messages';
import { AudioConfig } from '../config/audio';
import { ConfigContext } from '../context/ConfigContext';
import analyticsService from '../services/analyticsService';
import { default as io, default as ioService } from '../services/ioService';
import {
  getUserId,
  default as localStorageService,
  default as storage,
} from '../services/localStorageService';
import { sendLocationRequest } from '../services/postMessageService';
import querystringService from '../services/querystringService';
import type {
  ChatActivityStatus,
  IDData,
  Message,
  MessageDirection,
  MessagePayload,
} from '../types/ChatWindow';
import type {
  ChatWindowState as ChatWindowTypeState,
  InitStateProps,
} from '../types/configs';
import type { MessageData } from '../types/io';
import Attachments from './Attachments';

type ChatWindowProps = {
  chatEnabled: boolean;
  onEnableChat: () => void;
  allowTextInput: boolean;
  messages: Array<Message>;
  addMessage: (...args: any[]) => void;
  changeWindowState: (state: ChatWindowTypeState) => void;
  sendWindowStatus: ({ windowToggle, windowType }) => void;
  onClose: (...args: any[]) => void;
  registeredEvents: {
    set: (...args: any[]) => void;
    get: (...args: any[]) => any;
    delete: (...args: any[]) => void;
  };
  fullscreen: boolean;
  setMessages: (id: string) => void;
  data?: unknown;
  initState: (props: InitStateProps) => void;
  changeChatActivityStatus: (status: ChatActivityStatus) => void;
  chatActivityStatus: ChatActivityStatus;
  bannerMessage?: string;
  bannerStatus: 'info' | 'error';
  toggleConnectionStateBanner: (status: boolean) => void;
};

export const ChatWindow: React.FC<
  ChatWindowProps & { hidden?: boolean; initWebSocket?: boolean }
> = ({
  hidden,
  initWebSocket,
  fullscreen,
  allowTextInput,
  chatEnabled,
  messages,
  onClose,
  onEnableChat,
  changeWindowState,
  addMessage,
  setMessages,
  registeredEvents,
  data,
  sendWindowStatus,
  initState,
  changeChatActivityStatus,
  chatActivityStatus,
  bannerStatus,
  bannerMessage,
  toggleConnectionStateBanner,
}) => {
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const elementClass = useMemo(
    () =>
      `chat-window-container ${fullscreen ? 'full-screen' : ''} ${chatEnabled ? 'blocked' : ''} ${hidden ? 'hidden' : ''}`,
    [fullscreen, chatEnabled, hidden],
  );

  const { setSendChatStatus } = useChatStatus();
  const [messageSound] = useState(new Audio(AudioConfig.messageSound));
  const [typingState, setTypingIndicator] = useState<
    'typing_on' | 'typing_off'
  >('typing_off');
  const [attachments, setAttachments] = useState([]);
  const [isSendMessageDisabled, setIsSendMessageDisabled] = useState(false);
  const [flowState, changeFlowState] = useState('');

  const {
    scheme: {
      header: { headerColor },
    },
    isSoundEnabled,
    refParam,
    revisionId,
    configId,
    defaultState,
    botId,
    enableWaitingForBotResponseBlocker,
  } = useContext(ConfigContext);

  // On iOS browsers, certain elements, especially those with position: fixed or z-index, are not rendered immediately,
  // especially if they are positioned off-screen or do not interact with the user.
  // This is a performance optimization in iOS browsers that prioritizes visible and interactive content.
  // By applying transform: translateZ(0) and temporarily hiding and showing the element (visibility: hidden to visibility: visible),
  // we force the GPU layer to render the element, ensuring that it is visible and interacts properly even without user interaction (scrolling or tapping).
  useEffect(() => {
    const el = document.getElementById('conversed-chat-widget-1848');
    if (el) {
      el.style.transform = 'translateZ(0)'; // triggers GPU layer
      el.style.visibility = 'hidden';
      requestAnimationFrame(() => {
        el.style.visibility = 'visible';
      });
    }
  }, []);

  useEffect(() => {
    // Clear any existing timer if the chat activity status changes to online,
    // this timer is used to close the chat window after idleTimeout value
    if (chatActivityStatus === 'online' && idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
  }, [chatActivityStatus]);

  const handleSendMessageDisabledSetter = useCallback(
    (value: boolean) => {
      // If this feature is disabled, then don't block the user from sending messages.
      // This option is configurable in the Webclient config modal.
      if (!enableWaitingForBotResponseBlocker) {
        return;
      }

      // If the bot is not in the LLM state, then this blocker feature is not needed.
      if (flowState !== 'llm' && value) {
        return;
      }

      setIsSendMessageDisabled(value);
    },
    [enableWaitingForBotResponseBlocker, flowState],
  );

  const requestData = useCallback((requestType: string) => {
    switch (requestType) {
      case 'location': {
        sendLocationRequest();
        break;
      }
      default: {
        break;
      }
    }
  }, []);

  const setUserID = useCallback(
    (data: IDData) => {
      try {
        const currentStorageItem = storage.get(configId);
        let dataToBeStored = { id: data.id };

        if (currentStorageItem) {
          const parsedData = JSON.parse(currentStorageItem);
          dataToBeStored = { ...parsedData, id: data.id };
        }
        storage.set(configId, dataToBeStored);
      } catch (_error) {
        storage.set(configId, { id: data.id });
      }
    },
    [configId],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Changing could break functionality
  const registerIOEvents = useCallback(() => {
    if (!registeredEvents.get()) {
      io.registerEvent({ event: 'text', callback: processMessage });
      io.registerEvent({ event: 'system', callback: processMessage });
      io.registerEvent({ event: 'image', callback: processMessage });
      io.registerEvent({
        event: 'imageWithButtons',
        callback: processMessage,
      });
      io.registerEvent({
        event: 'textWithButtons',
        callback: processMessage,
      });
      io.registerEvent({ event: 'carousel', callback: processMessage });
      io.registerEvent({ event: 'auth', callback: processMessage });
      io.registerEvent({ event: 'action', callback: processMessage });
      io.registerEvent({ event: 'user id', callback: processMessage });
      io.registerEvent({ event: 'user', callback: processMessage });
      io.registerEvent({ event: 'dataRequest', callback: processMessage });
      io.registerEvent({
        event: 'updateUserId',
        callback: processMessage,
      });
      io.registerEvent({ event: 'feedback', callback: processMessage });
      io.registerEvent({ event: 'dropdown', callback: processMessage });
      io.registerEvent({ event: 'end', callback: processMessage });
      io.registerEvent({ event: 'idleOptions', callback: processMessage });
      io.registerEvent({ event: 'flowState', callback: processMessage });
      registeredEvents.set();
    }
  }, [registeredEvents]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Changing could break functionality
  const processMessage = useCallback(
    (direction: MessageDirection, params: MessagePayload) => {
      switch (params.type) {
        case 'id': {
          setUserID(params.data);
          return;
        }
        case 'dataRequest': {
          requestData(params.data);
          return;
        }
        case 'action': {
          setTypingIndicator(params.data);
          return;
        }
        case 'updateUserId': {
          registeredEvents.delete();
          setUserID({ id: params.data });
          io.reInitialize(configId, registerIOEvents);
          setMessages(params.data);
          return;
        }
        case 'end': {
          changeWindowState('closed');
          sendWindowStatus({ windowToggle: false, windowType: defaultState });
          initState({
            windowType: defaultState,
            windowToggle: false,
          });
          resetUserAndReInitIOService();
          return;
        }
        case 'idleOptions': {
          // Clear any existing timer
          if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
          }

          if (typeof params?.data?.idleTimeout === 'undefined') {
            return;
          }

          // Start a new timer based on idleTimeout
          const idleTimeoutInMs = params.data.idleTimeout * 60 * 1000;
          idleTimerRef.current = setTimeout(() => {
            if (params.data.closeAfterIdleTimeout) {
              changeWindowState('closed');
            }

            sendWindowStatus({
              windowToggle: !params.data.closeAfterIdleTimeout,
              windowType: defaultState,
            });
            initState({
              windowType: defaultState,
              windowToggle: !params.data.closeAfterIdleTimeout,
            });

            resetUserAndReInitIOService();
          }, idleTimeoutInMs);

          return;
        }
        case 'flowState': {
          if (params.data.state !== 'llm') {
            // If the user is blocked from sending messages, then unblock the user when the bot is not in the LLM state
            handleSendMessageDisabledSetter(false);
          }

          changeFlowState(params.data.state);
          return;
        }
        default: {
          handleSendMessageDisabledSetter(false);
          if (isSoundEnabled) {
            messageSound?.play().catch((e) => {
              // This happens because browsers may block autoplay of audio due to user interaction requirements or other restrictions.
              console.error('Playing sound is disabled by the browser', e);
            });
          }

          addMessage(direction, params);
        }
      }
    },
    [
      setUserID,
      requestData,
      registerIOEvents,
      setMessages,
      addMessage,
      isSoundEnabled,
      messageSound?.play,
      registeredEvents?.delete,
      changeWindowState,
      sendWindowStatus,
      initState,
      defaultState,
      handleSendMessageDisabledSetter,
      configId,
    ],
  );

  const resetUserAndReInitIOService = useCallback(() => {
    localStorageService.delete(configId);
    registeredEvents.delete();
    ioService.reInitialize(configId, registerIOEvents);
    handleSendMessageDisabledSetter(false);
  }, [
    registerIOEvents,
    registeredEvents,
    handleSendMessageDisabledSetter,
    configId,
  ]);

  const registerIOEventsOnChatEnabled = useCallback(async () => {
    io.reInitialize(configId, registerIOEvents);
    handleSendMessageDisabledSetter(false);
    onEnableChat();
  }, [
    onEnableChat,
    registerIOEvents,
    handleSendMessageDisabledSetter,
    configId,
  ]);

  const handleAttachmentCancel = useCallback(
    (index: number) => {
      setAttachments(attachments.splice(index, 1));
    },
    [attachments],
  );

  const getIOSubmitPayload = useCallback(
    (value, postbackType?: string): MessageData => {
      const userId = getUserId(configId);
      if (typeof value === 'string') {
        handleSendMessageDisabledSetter(true);
        return {
          id: userId,
          type: 'message',
          message: value,
        };
      }

      const response: MessageData = {
        id: userId,
        type: 'postback',
        message: value,
      };

      if (postbackType) {
        Object.assign(response, {
          postbackType: postbackType,
        });
      }

      return response;
    },
    [handleSendMessageDisabledSetter, configId],
  );

  const submitHandler = useCallback(
    ({ value, attachments, shouldHideMessage, postbackType }) => {
      if (isSendMessageDisabled) {
        throw new Error('Waiting for the bot response, please wait');
      }

      if (value && value !== '') {
        const payload: MessagePayload = {
          type: 'text',
          data: {
            text: typeof value === 'string' ? value : value.title,
          },
        };

        const close =
          value?.options?.webclient?.close || value?.options?.close || false;

        // * If webclient options is set for button carry that over to the next message
        if (typeof value?.options !== 'undefined') {
          Object.assign(payload, {
            options: {
              webclient: {
                ...payload?.options?.webclient,
                ...(value.options?.webclient || value.options),
              },
            },
          });
        }

        if (close) {
          changeWindowState('closed');
        }

        io.send({
          event: 'message',
          data: getIOSubmitPayload(value, postbackType),
        });

        if (!shouldHideMessage) addMessage('in', payload);
      }

      if (Array.isArray(attachments) && attachments.length > 0) {
        attachments.forEach((attachment) => {
          io.send({
            event: 'image',
            data: {
              id: getUserId(configId),
              type: 'image',
              data: attachment,
            },
          });

          const imagePayload: MessagePayload = {
            type: 'image',
            data: {
              url: attachment.base64,
            },
          };

          addMessage('in', imagePayload);
        });
      }
    },
    [
      changeWindowState,
      addMessage,
      getIOSubmitPayload,
      isSendMessageDisabled,
      configId,
    ],
  );

  const getWebSocketOptions = useCallback(() => {
    const options = {
      ...querystringService.getAsRefOptions(),
      analytics: analyticsService.createAnalyticData(),
      data,
      metadata: {},
    };

    if (refParam) {
      Object.assign(options, {
        ref: {
          ref: refParam,
          source: 'conversed-webclient',
        },
      });
    }

    if (revisionId) {
      Object.assign(options, {
        metadata: {
          ...options?.metadata,
          revision: revisionId,
        },
      });
    }

    if (configId) {
      Object.assign(options, {
        metadata: {
          ...options?.metadata,
          configId,
        },
      });
    }

    return options;
  }, [refParam, revisionId, data, configId]);

  useEffect(() => {
    if (!initWebSocket) {
      return;
    }

    const URL = Api.CONVERSED_SERVER;
    // Initialize the socket connection
    ioService.init(
      URL,
      botId,
      configId,
      registerIOEvents,
      toggleConnectionStateBanner,
    );

    return () => {
      // * Remove listeners
      ioService.removeListeners();
      registeredEvents.delete();
    };
  }, [
    initWebSocket,
    registerIOEvents,
    toggleConnectionStateBanner,
    registeredEvents.delete,
    botId,
    configId,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Changing would break functionality
  useEffect(() => {
    if (initWebSocket) {
      const options = getWebSocketOptions();
      const id = getUserId(configId);
      // Check if this connection is already established via the 'connected' flag in ioService
      // Only send initialization messages if there is no connection or we're deliberately starting a new chat
      const isNewSession = !io?.isConnected() || !id;

      // * If the user has an id, then restart the previous user chat session, otherwise start a new chat session
      if (id && isNewSession) {
        io.send({
          event: 'restartFlow',
          data: { id, type: 'restart', options },
        });

        handleSendMessageDisabledSetter(false);
      } else if (!id) {
        io.send({
          event: 'new user',
          data: { id, type: 'intro', options },
        });
      }
    }
  }, [initWebSocket]);

  if (!chatEnabled) {
    return (
      <div
        className={elementClass}
        style={{
          borderColor: headerColor,
        }}>
        <Blocker onEnableChat={registerIOEventsOnChatEnabled} />
      </div>
    );
  }

  return (
    <div className={elementClass}>
      <ChatTitle
        onClose={onClose}
        bannerMessage={bannerMessage}
        bannerStatus={bannerStatus}
      />
      <Messages
        messages={messages}
        submitHandler={submitHandler}
        typingState={typingState}
      />
      <Attachments
        show={attachments.length > 0}
        attachments={attachments}
        handleAttachmentCancelClick={handleAttachmentCancel}
      />
      <MessageBox
        allowTextInput={allowTextInput}
        setAttachments={setAttachments}
        attachments={attachments}
        submitHandler={submitHandler}
        messages={messages}
        setSendChatStatus={setSendChatStatus}
        changeChatActivityStatus={changeChatActivityStatus}
      />
    </div>
  );
};

export default ChatWindow;
